import { userUtils } from '@/helpers';
import Linker from '@/helpers/url';
import { Button, Flex, FlexProps, Typography, useToast } from '@alpine-iq/ui-library';
import React from 'react';

type Props = Omit<FlexProps, 'onClick'> & {
	text?: string;
	hideButton?: boolean;
	buttonText?: string;
	onClick?: () => void;
	blank?: boolean;
	justButton?: boolean;
};

export default function UpsellBanner({ text, buttonText, hideButton, onClick, blank, ...props }: Props) {
	const { addToast } = useToast();
	const handleUpgradeClick = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		if (onClick) return onClick();
		if (!userUtils.hasRoleView('owner')) {
			return addToast({
				type: 'danger',
				message: 'You do not have the permission to perform this action. Please ask an Owner to upgrade your account.',
				placement: 'top',
			});
		}
		const linker = new Linker('/settings/:uid/billing/general');
		if (e.button === 1 || blank) linker.open(true);
		else linker.openWithHistory();
	};

	const UpsellButton = () => {
		return (
			<>
				<Button
					variant="primary"
					size="medium"
					onMouseDown={handleUpgradeClick}
					onClick={onClick}
				>
					{buttonText ?? 'Upgrade'}
				</Button>
			</>
		);
	};
	if (props.justButton) return <UpsellButton />;

	return (
		<>
			<Flex
				css={`
					border-radius: var(--radius-sm, 8px);
					background: var(--indigo100, #efefff);
					display: flex;
					padding: 16px;
					align-items: center;
					gap: 16px;
					align-self: stretch;
				`}
				justify="space-between"
				{...props}
			>
				<Flex
					align="center"
					gap={16}
				>
					<LightningBanner />
					<Typography>{text ?? 'Upgrade to unlock this feature'}</Typography>
				</Flex>
				{!hideButton && <UpsellButton />}
			</Flex>
		</>
	);
}

export const LightningBanner = () => (
	<Flex
		css={`
			display: flex;
			padding: 4px;
			align-items: center;
			gap: 10px;
			border-radius: 47px;
			background: var(--surface-secondary, #fafbfc);
			box-shadow: 0px 4px 20px 0px rgba(98, 98, 245, 0.42);
		`}
	>
		<Flex
			css={`
				display: flex;
				padding: 8px;
				align-items: center;
				border-radius: 32.5px;
				background: var(--indigo100, #efefff);
			`}
		>
			<LightningIcon />
		</Flex>
	</Flex>
);

export const LightningIcon = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="22"
		height="22"
		viewBox="0 0 22 22"
		fill="none"
	>
		<path
			fill-rule="evenodd"
			clip-rule="evenodd"
			d="M12.2179 2.31929C12.5441 2.46034 12.7351 2.78816 12.6883 3.12663L11.9276 8.62976L17.4599 8.62975C17.6748 8.62972 17.8854 8.6297 18.0561 8.64368C18.2158 8.65675 18.5103 8.69029 18.7677 8.87836C19.0695 9.09893 19.2561 9.44473 19.2498 9.82214C19.2444 10.1486 19.0689 10.3939 18.9767 10.5147C18.8771 10.6451 18.7402 10.7936 18.6037 10.9416L10.7292 19.4863C10.4928 19.7428 10.1082 19.8218 9.78206 19.6807C9.45589 19.5397 9.2649 19.2118 9.31169 18.8734L10.0724 13.3702L4.54007 13.3702C4.32523 13.3703 4.11461 13.3703 3.94387 13.3563C3.78422 13.3433 3.4897 13.3097 3.23232 13.1216C2.93046 12.9011 2.74386 12.5553 2.75015 12.1779C2.7556 11.8514 2.93106 11.6061 3.02328 11.4853C3.12288 11.3549 3.25983 11.2064 3.39625 11.0584C3.40287 11.0512 3.4095 11.044 3.41612 11.0369L11.2708 2.51367C11.5072 2.25717 11.8918 2.17825 12.2179 2.31929ZM4.86003 11.8196H11C11.2342 11.8196 11.4571 11.9159 11.6116 12.0838C11.766 12.2516 11.837 12.4748 11.8064 12.6962L11.2743 16.5452L17.14 10.1804H11C10.7658 10.1804 10.5429 10.0841 10.3884 9.91624C10.234 9.74835 10.163 9.52516 10.1936 9.30378L10.7257 5.45477L4.86003 11.8196Z"
			fill="#6262F5"
		/>
		<path
			d="M11 11.8196H4.86003L10.7257 5.45477L10.1936 9.30378C10.163 9.52516 10.234 9.74835 10.3884 9.91624C10.5429 10.0841 10.7658 10.1804 11 10.1804H17.14L11.2743 16.5452L11.8064 12.6962C11.837 12.4748 11.766 12.2516 11.6116 12.0838C11.4571 11.9159 11.2342 11.8196 11 11.8196Z"
			fill="#6262F5"
		/>
	</svg>
);
